import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Input, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import TextArea from 'antd/es/input/TextArea';
import React, {useContext} from 'react';
import ReactQuill from 'react-quill';

import NumberInput from '../../../shared/NumberInput';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import {sortedOptions} from '../constants/commonFunctions';
import SubmitDisabledContext from '../constants/SubmitDisabledContext';
import {inputType} from '../interface/inputType';

import PromptSteps from './PromptSteps';
import QuestionnaireSwitch from './QuestionnaireSwitch';

const InputGrid: React.FC<{
  field: any;
}> = ({field}) => {
  const {NUMBER_COL, TEXTAREA_COL, TEXT_COL} = inputType;
  const FormInstance = useFormInstance();
  const {getFieldValue, setFieldValue} = FormInstance;
  const companyId = useCompanyStore.getState().companyId;
  const userId = useUserStore((state) => state.userDetails._id);
  const setSubmitDisabled = useContext(SubmitDisabledContext);

  const tooltipCSS = {
    width: 'auto',
    maxWidth: '500px',
    height: 'auto',
    padding: '12px'
  };

  switch (field.fieldType) {
    case TEXT_COL:
      return (
        <Col span={12}>
          <Form.Item
            labelCol={{span: 24}}
            name={field.fieldName}
            label={field.fieldLabel}
            tooltip={
              field.tooltip
                ? {title: field.tooltip, icon: <InfoCircleOutlined />}
                : ''
            }
          >
            {field.askAIkey ? (
              <ReactQuill
                placeholder={field.placeHolder}
                readOnly={field.disabled}
                style={{width: '100%'}}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
          {field.askAIkey && (
            <Form.Item noStyle>
              <PromptSteps
                field={field}
                getFieldValue={getFieldValue}
                setFieldValue={setFieldValue}
                setSubmitDisabled={setSubmitDisabled}
                userId={userId}
                companyId={companyId}
              />
            </Form.Item>
          )}
        </Col>
      );
    case TEXTAREA_COL:
      return (
        <Col span={24}>
          <Form.Item
            labelCol={{span: 24}}
            name={field.fieldName}
            label={field.fieldLabel}
            tooltip={
              field.tooltip
                ? {title: field.tooltip, icon: <InfoCircleOutlined />}
                : ''
            }
            style={field.askAIkey ? {marginBottom: '0px'} : {}}
          >
            {field.askAIkey ? (
              <ReactQuill
                placeholder={field.placeHolder}
                readOnly={field.disabled}
                style={{width: '100%'}}
              />
            ) : (
              <TextArea
                style={{verticalAlign: 'top'}}
                autoSize={{minRows: 4, maxRows: 10}}
              />
            )}
          </Form.Item>
          {field.askAIkey && (
            <Form.Item noStyle>
              <PromptSteps
                field={field}
                getFieldValue={getFieldValue}
                setFieldValue={setFieldValue}
                setSubmitDisabled={setSubmitDisabled}
                userId={userId}
                companyId={companyId}
              />
            </Form.Item>
          )}
        </Col>
      );
    case NUMBER_COL:
      return (
        <Col span={12}>
          <Form.Item
            name={field.units ? [field.fieldName, 'value'] : field.fieldName}
            label={field.fieldLabel}
            labelCol={{span: 24}}
            tooltip={
              field.tooltip
                ? {
                    title: field.tooltip,
                    icon: <InfoCircleOutlined />,
                    overlayStyle: tooltipCSS
                  }
                : ''
            }
          >
            <NumberInput
              min={field.min}
              max={field.max}
              defaultValue={field.defaultValue}
              placeholder={field.placeHolder}
              disabled={field.disabled}
              isFormatted={field?.isFormatted}
              addonAfter={
                !field.units ? null : typeof field.units === 'string' ? (
                  <Form.Item
                    name={field.units && [field.fieldName, 'unit']}
                    initialValue={field.units}
                    noStyle
                  >
                    {field.units}
                  </Form.Item>
                ) : (
                  <Form.Item
                    name={field.units && [field.fieldName, 'unit']}
                    noStyle
                  >
                    <Select
                      style={{width: 140}}
                      placeholder="Select Unit"
                      options={sortedOptions(field.units)}
                    />
                  </Form.Item>
                )
              }
            />
          </Form.Item>
          {field.askAIkey && (
            <Form.Item noStyle>
              <PromptSteps
                field={field}
                getFieldValue={getFieldValue}
                setFieldValue={setFieldValue}
                setSubmitDisabled={setSubmitDisabled}
                userId={userId}
                companyId={companyId}
              />
            </Form.Item>
          )}
        </Col>
      );

    default:
      return (
        <Col span={24}>
          <QuestionnaireSwitch field={field} />
        </Col>
      );
  }
};

export default InputGrid;
